import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import { generateQAFromDownloaded, getDownloadedWebpages, deleteDownloadedWebpage } from '../../services/api';
import { DownloadedWebpage } from '../../types/index';
import DownloadedWebpagesList from '../DownloadedWebpagesList';

interface QAGenerationTabProps {
  aiId: string;
  collectionId: string;
  downloadedWebpages: DownloadedWebpage[];
  onDownloadedWebpagesChange: (webpages: DownloadedWebpage[]) => void;
  onQAGenerated: (addedPairs: any[]) => void;
  onRefreshQnaPairs?: () => void;
}

const QAGenerationTab: React.FC<QAGenerationTabProps> = ({
  aiId,
  collectionId,
  downloadedWebpages,
  onDownloadedWebpagesChange,
  onQAGenerated,
  onRefreshQnaPairs
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [processImages, setProcessImages] = useState(true); // New state for image processing
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filter only downloaded webpages that are ready for processing
  const availableWebpages = downloadedWebpages.filter(webpage => 
    webpage.status === 'completed'
  );

  // Reset selection when available webpages change
  useEffect(() => {
    const validSelectedIds = selectedIds.filter(id => 
      availableWebpages.some(webpage => webpage.id === id)
    );
    if (validSelectedIds.length !== selectedIds.length) {
      setSelectedIds(validSelectedIds);
    }
  }, [availableWebpages, selectedIds]);

  const fetchDownloadedWebpages = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getDownloadedWebpages(aiId, collectionId, { limit: 100 });
      const webpages = response.data.data || [];
      
      onDownloadedWebpagesChange(webpages);
    } catch (err: any) {
      console.error('Error fetching downloaded webpages:', err);
      setError('Failed to load downloaded webpages: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQA = async () => {
    if (selectedIds.length === 0) {
      setError('Please select at least one downloaded webpage');
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      setSuccess(null);

      console.log('Generating Q&A from downloaded webpages:', selectedIds);

      const response = await generateQAFromDownloaded(aiId, collectionId, {
        downloadedWebpageIds: selectedIds,
        processImages
      });

      console.log('Q&A generation response:', response.data);

      const { addedPairs, processedWebpages, totalPairsGenerated } = response.data.data;

      setSuccess(
        `Successfully generated ${totalPairsGenerated} Q&A pairs from ${processedWebpages} webpage${processedWebpages > 1 ? 's' : ''}`
      );

      // Notify parent component about new Q&A pairs
      onQAGenerated(addedPairs);

      // Refresh the Q&A pairs list to show the updated/replaced pairs
      if (onRefreshQnaPairs) {
        onRefreshQnaPairs();
      }

      // Clear selection after successful generation
      setSelectedIds([]);

      // Refresh the downloaded webpages list to update statuses
      await fetchDownloadedWebpages();

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 5000);

    } catch (err: any) {
      console.error('Error generating Q&A:', err);
      setError('Failed to generate Q&A pairs: ' + (err.response?.data?.error || err.message));
      
      // Refresh the list to update any status changes
      await fetchDownloadedWebpages();
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteDownloadedWebpage = async (id: string) => {
    try {
      await deleteDownloadedWebpage(aiId, collectionId, id);
      
      // Update downloaded webpages through parent
      const updatedWebpages = downloadedWebpages.filter(webpage => webpage.id !== id);
      onDownloadedWebpagesChange(updatedWebpages);
      
      // Remove from selection if selected
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
      
      setSuccess('Downloaded webpage deleted successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);

    } catch (err: any) {
      console.error('Error deleting downloaded webpage:', err);
      setError('Failed to delete downloaded webpage: ' + (err.response?.data?.error || err.message));
    }
  };

  const getSelectionSummary = () => {
    const selectedWebpages = availableWebpages.filter(webpage => selectedIds.includes(webpage.id));
    const imageCount = selectedWebpages.reduce((total, webpage) => {
      return total + (webpage.image_data?.totalImages || 0);
    }, 0);
    
    return {
      webpageCount: selectedWebpages.length,
      imageCount
    };
  };

  const summary = getSelectionSummary();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body1" color="text.primary" sx={{ mb: 3 }}>
        Select downloaded webpages and generate Q&A pairs from their content using AI.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Generation Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Q&A Generation Settings
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateQA}
              disabled={generating || selectedIds.length === 0}
              startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
            >
              {generating ? 'Generating...' : 'Generate Q&A Pairs'}
            </Button>
          </Box>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={processImages}
                onChange={(e) => setProcessImages(e.target.checked)}
                disabled={generating}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" component="span">
                  Process images with AI
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  Use downloaded image descriptions when generating Q&A pairs
                </Typography>
              </Box>
            }
          />
        </Box>

        {summary.webpageCount > 0 && (
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Selection Summary:</strong> {summary.webpageCount} webpage{summary.webpageCount > 1 ? 's' : ''}
              {summary.imageCount > 0 && ` with ${summary.imageCount} image${summary.imageCount > 1 ? 's' : ''}`}
              {' selected for Q&A generation'}
            </Typography>
          </Box>
        )}
      </Paper>

      <Divider sx={{ mb: 3 }} />

      {/* Downloaded Webpages List */}
      <DownloadedWebpagesList
        downloadedWebpages={availableWebpages}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onDelete={handleDeleteDownloadedWebpage}
        onRefresh={fetchDownloadedWebpages}
        onRefreshQnaPairs={onRefreshQnaPairs}
        loading={loading}
        error={error}
        showSelection={true}
      />

      {availableWebpages.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No downloaded webpages available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Go to the "Download Webpages" tab to download some content first
          </Typography>
        </Paper>
      )}

      {generating && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
          <Typography variant="body2" color="warning.contrastText">
            ⏳ Generating Q&A pairs... This may take a few minutes depending on the content size.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default QAGenerationTab;